import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Chip,
  Skeleton,
  Button,
  Stack,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  FolderShared as ResourceIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { Quiz as WorksheetIcon } from "@mui/icons-material";
import api from "../../api";

const STUDENT_PREFIX = process.env.REACT_APP_STUDENT_PREFIX || "student";

/**
 * Who added the file. Admin uploads are course material; the rest come from the group's staff.
 * A student should not have to know which staff member added a file to find it, so both kinds
 * sit in one list and this only labels the origin.
 */
const SOURCE_LABEL = {
  admin: { label: "Course material", color: "#1565c0", bg: "#1565c018" },
  tutor: { label: "From your tutor", color: "#2e7d32", bg: "#2e7d3218" },
  assistant: { label: "From your tutor", color: "#2e7d32", bg: "#2e7d3218" },
};

const subjectColor = (key) =>
  ({
    maths: "#1565c0",
    english: "#6a1b9a",
    "verbal-reasoning": "#2e7d32",
    "non-verbal-reasoning": "#ef6c00",
  }[key] || "#455a64");

/**
 * All approved content for the student's group and week — admin course material and tutor uploads
 * in one list, ordered course material first (the server does that ordering).
 *
 * Only admin-approved content reaches this — the endpoint is default-deny
 * (ContentVerification::filterVisibleResourceIds). Renders nothing at all when there is none, so
 * it never adds empty chrome to the Homework page.
 *
 * @param {number|string} [weekId]  restrict to one plan week; omitted shows everything for the group
 * @param {number|string} [subjectId]
 */
export default function TutorWeekResources({ weekId, subjectId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (weekId) params.plan_week_id = weekId;
      if (subjectId) params.subject_id = subjectId;
      const res = await api.get("student/group-resources", { params });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch {
      // A tutor-content failure must not break the Homework page around it.
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [weekId, subjectId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <Skeleton variant="rounded" height={64} sx={{ mb: 2 }} />;
  }

  // Nothing to show — stay invisible rather than render an empty section.
  if (rows.length === 0) {
    return null;
  }

  return (
    <Accordion
      defaultExpanded
      elevation={0}
      sx={{
        mb: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ResourceIcon fontSize="small" color="primary" />
          <Typography variant="subtitle1" fontWeight={700}>
            This week's work and content
          </Typography>
          <Chip size="small" label={rows.length} color="primary" />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1.5}>
          {rows.map((r) => (
            <Paper
              key={r.id}
              variant="outlined"
              sx={{ p: 1.5, borderRadius: 2, display: "flex", gap: 2, alignItems: "center" }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" spacing={0.5} sx={{ mb: 0.5, flexWrap: "wrap", gap: 0.5 }}>
                  {SOURCE_LABEL[r.source] && (
                    <Chip
                      size="small"
                      label={SOURCE_LABEL[r.source].label}
                      sx={{
                        bgcolor: SOURCE_LABEL[r.source].bg,
                        color: SOURCE_LABEL[r.source].color,
                        fontWeight: 700,
                      }}
                    />
                  )}
                  {r.subject?.name && (
                    <Chip
                      size="small"
                      label={r.subject.name}
                      sx={{
                        bgcolor: `${subjectColor(r.subject.key)}18`,
                        color: subjectColor(r.subject.key),
                        fontWeight: 700,
                      }}
                    />
                  )}
                  {r.topic?.name && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={r.subtopic?.name ? `${r.topic.name} › ${r.subtopic.name}` : r.topic.name}
                    />
                  )}
                  {r.week?.week_number != null && (
                    <Chip size="small" variant="outlined" label={r.week.label || `Week ${r.week.week_number}`} />
                  )}
                  {r.kind === "worksheet" && (
                    <Chip size="small" icon={<WorksheetIcon />} label="Worksheet" variant="outlined" />
                  )}
                  {r.due_at && (
                    <Chip
                      size="small"
                      label={`Due ${new Date(r.due_at).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}`}
                      sx={{
                        fontWeight: 700,
                        bgcolor: new Date(r.due_at) < new Date() ? "#fef2f2" : "#eef2ff",
                        color: new Date(r.due_at) < new Date() ? "#b91c1c" : "#4338ca",
                      }}
                    />
                  )}
                </Stack>
                <Typography variant="body1" fontWeight={600} noWrap>
                  {r.title}
                </Typography>
                {r.description && (
                  <Typography variant="body2" color="text.secondary">
                    {r.description}
                  </Typography>
                )}
                {r.tutor && r.source !== "admin" && (
                  <Typography variant="caption" color="text.secondary">
                    Shared by {r.tutor}
                  </Typography>
                )}
              </Box>
              {/* A worksheet allocated to the week opens in the worksheet player. */}
              {r.kind === "worksheet" && r.worksheet_id && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => navigate(`/${STUDENT_PREFIX}/work-sheet/${r.worksheet_id}/take`)}
                  sx={{ flexShrink: 0 }}
                >
                  Start
                </Button>
              )}
              {r.file_url && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  component="a"
                  href={r.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ flexShrink: 0 }}
                >
                  Open
                </Button>
              )}
            </Paper>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
